import {
  deadlineStatus,
  exceedsCashLimit,
  formatDate,
  formatMoney,
  isoMonthSchema,
  monthOf,
  type NoticeObligation,
  type Operation,
  operationStatus,
  PAYMENT_METHOD_LABELS,
  summarizeOperations,
} from "@pcj/shared";
import { FileText, Plus, Trash2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { MonthHeader } from "../../components/MonthFolders";
import { Banner, Button, Card, DownloadLink, EmptyState, Pill, Table, td, th } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { DEADLINE_TONES, deadlineLabel, plural } from "../../lib/format";
import { operationsReportUrl } from "../../lib/queries";
import { useCompany } from "./CompanyLayout";
import { FilingDialog } from "./FilingDialog";
import { OperationForm } from "./OperationForm";

/** The operations of one month (route `operations/:month`). */
export function OperationsMonthPage() {
  const { month = "" } = useParams();
  const state = useCompany();
  const { operations, notices, clientById, noticeIndex, thresholds, today, startMonth, companyId } = state;
  const { can, readOnly, actions, clients } = state;
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [filing, setFiling] = useState<NoticeObligation | null>(null);
  const currentMonth = monthOf(today);

  if (!isoMonthSchema.safeParse(month).success || month > currentMonth)
    return <Navigate to=".." relative="path" replace />;

  const monthOperations = operations.filter((op) => monthOf(op.date) === month);
  const summary = summarizeOperations({
    operations: monthOperations,
    notices: notices.filter((n) => n.month === month),
    thresholds,
    today,
  });
  const oldest = operations.reduce((min, op) => (monthOf(op.date) < min ? monthOf(op.date) : min), startMonth);

  function remove(op: Operation) {
    if (
      window.confirm(
        `¿Eliminar la operación del ${formatDate(op.date)} por ${formatMoney(op.amount)}? Quedará registrado en la bitácora.`,
      )
    ) {
      actions.deleteOperation.mutate(op.id);
    }
  }

  function status(op: Operation) {
    const current = operationStatus(op, noticeIndex, thresholds);
    if (current.kind === "notice") {
      const notice = current.notice;
      if (notice.filed) return <Pill tone="green">Aviso presentado{op.noticeFolio ? ` · ${op.noticeFolio}` : ""}</Pill>;
      const deadline = deadlineStatus(notice.deadline, today);
      const pill = (
        <Pill tone={DEADLINE_TONES[deadline]} icon={TriangleAlert}>
          {notice.kind === "accumulated" ? "Aviso acumulado" : "Requiere Aviso"} ·{" "}
          {deadlineLabel(deadline, notice.deadline, today)}
        </Pill>
      );
      return can("manageCompliance") ? (
        <button type="button" onClick={() => setFiling(notice)} title="Registrar la presentación del Aviso">
          {pill}
        </button>
      ) : (
        pill
      );
    }
    if (current.kind === "covered")
      return <Pill tone="neutral">En aviso acumulado del {formatDate(current.notice.date)}</Pill>;
    return current.kind === "identification" ? (
      <Pill tone="amber">Requiere identificación</Pill>
    ) : (
      <Pill>Sin umbral</Pill>
    );
  }

  const figures = [
    ["Operaciones", String(summary.operations)],
    ["Monto sin IVA", formatMoney(summary.amount)],
    ["En efectivo", formatMoney(summary.byPaymentMethod.cash.amount)],
    [
      "Avisos del mes",
      summary.notices.total === 0
        ? "Ninguno"
        : `${summary.notices.total}${summary.notices.pending > 0 ? ` · ${plural(summary.notices.pending, "pendiente", "pendientes")}` : ""}`,
    ],
  ];

  return (
    <div className="space-y-4">
      <MonthHeader
        month={month}
        title="Operaciones de"
        subtitle="Montos sin IVA"
        first={oldest}
        last={currentMonth}
        actions={
          <>
            {can("capture") && !readOnly && (
              <Button icon={Plus} onClick={() => setFormOpen((v) => !v)} disabled={clients.length === 0}>
                Nueva operación
              </Button>
            )}
            <DownloadLink href={operationsReportUrl(companyId, month)}>Resumen del mes (PDF)</DownloadLink>
          </>
        }
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {figures.map(([label, value]) => (
          <Card key={label} className="p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
          </Card>
        ))}
      </div>
      {formOpen && (
        <OperationForm
          onDone={(created) => {
            setFormOpen(false);
            if (created && monthOf(created.date) !== month)
              navigate(`../${monthOf(created.date)}`, { relative: "path" });
          }}
        />
      )}
      {actions.deleteOperation.error && <Banner tone="error">{errorMessage(actions.deleteOperation.error)}</Banner>}

      {monthOperations.length === 0 ? (
        <EmptyState icon={FileText} title="No hay operaciones registradas en este mes" />
      ) : (
        <Table>
          <thead className="bg-gray-50">
            <tr>
              <th className={th}>Fecha</th>
              <th className={th}>Cliente</th>
              <th className={th}>Monto sin IVA</th>
              <th className={th}>Forma de pago</th>
              <th className={th}>Estatus</th>
              <th className={th} aria-label="Acciones" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {monthOperations.map((op) => (
              <tr key={op.id} className="hover:bg-gray-50">
                <td className={`${td} whitespace-nowrap text-gray-600`}>{formatDate(op.date)}</td>
                <td className={td}>
                  <p className="font-medium text-gray-900">{clientById.get(op.clientId)?.name ?? "—"}</p>
                  {op.description && <p className="text-xs text-gray-500">{op.description}</p>}
                </td>
                <td className={`${td} whitespace-nowrap text-gray-800`}>{formatMoney(op.amount)}</td>
                <td className={td}>
                  <span className="text-gray-600">{PAYMENT_METHOD_LABELS[op.paymentMethod]}</span>
                  {exceedsCashLimit(op, thresholds) && (
                    <span className="ml-2">
                      <Pill tone="red">Excede tope</Pill>
                    </span>
                  )}
                </td>
                <td className={td}>{status(op)}</td>
                <td className={`${td} text-right`}>
                  {can("delete") && (
                    <Button
                      variant="danger"
                      size="sm"
                      icon={Trash2}
                      onClick={() => remove(op)}
                      aria-label="Eliminar operación"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <FilingDialog
        open={filing !== null}
        title="Registrar Aviso presentado"
        description={
          filing
            ? `Aviso de ${clientById.get(filing.clientId)?.name ?? "cliente"} por ${formatMoney(filing.amount)} (sin IVA), con fecha límite ${formatDate(filing.deadline)}.`
            : ""
        }
        pending={actions.updateOperation.isPending}
        error={actions.updateOperation.error}
        onClose={() => setFiling(null)}
        onConfirm={(folio) =>
          filing &&
          actions.updateOperation.mutate(
            { id: filing.triggerOperationId, noticeFiled: true, noticeFolio: folio },
            { onSuccess: () => setFiling(null) },
          )
        }
      />
    </div>
  );
}
